import Inherit from '../../core/inherit';
import ShelfController from '../../core/shelf.controller';
import { ModernStores, TraditionalStores } from '../common';
import StoresDelegate from './stores.delegate';
import { User } from '../../data';
import dayjs from 'dayjs';
import { isFeatureEnabled } from '../../company/session';
import { FEATURES } from '../../company/features';

class StoresController extends Inherit(ShelfController, StoresDelegate) {
    shouldComponentUpdate(nextProps, nextState) {
        return (
            this.state.clients !== nextState.clients ||
            this.state.stores !== nextState.stores ||
            this.state.rows !== nextState.rows ||
            this.state.loading !== nextState.loading ||
            this.state.open !== nextState.open ||
            this.state.disabled !== nextState.disabled ||
            this.state.tab !== nextState.tab ||
            this.state.selectedRowKeys !== nextState.selectedRowKeys ||
            this.state.bulkDeleting !== nextState.bulkDeleting ||
            this.state.visitedStoreIds !== nextState.visitedStoreIds ||
            this.state.mapDateRange !== nextState.mapDateRange ||
            this.state.mapRecencyLoading !== nextState.mapRecencyLoading
        );
    }

    async initialize() {
        let clients = [];
        let stores = [];

        if (!this.isAuthenticated) {
            this.navigateLogin();
            return;
        }

        const currentUser = User.getInstance();
        const isClientUser = currentUser?.userType === 'client';
        const clientId = currentUser?.client;

        if (isClientUser) {
            if (clientId) {
                const rawStores = (await this.data.fetchStoresByClient(clientId)) || [];
                stores = await this.storeTableData(rawStores);
            }
        } else {
            clients = (await this.data.fetchClients()) || [];
            stores = await this.fetchStoresOptimized();
        }

        this.setState({ clients, stores, rows: stores, loading: false, isClientUser });
        await this.loadMapRecency();
    }

    /**
     * Fetch stores using the optimized cached endpoint
     * @param {boolean} forceRefresh - Force refresh the cache
     */
    async fetchStoresOptimized(forceRefresh = false) {
        try {
            const response = await this.data.fetchStoresCached(forceRefresh);
            
            if (response?.success && response?.data?.stores) {
                const storeList = response.data.stores;
                const stores = await this.storeTableData(storeList);
                
                return stores;
            }
            
            // Fallback to old method if cached endpoint fails
            const storeList = (await this.data.fetchAllStores(true)) || [];
            return await this.storeTableData(storeList);
        } catch (error) {
            // Fallback to old method
            const storeList = (await this.data.fetchAllStores(true)) || [];
            return await this.storeTableData(storeList);
        }
    }

    async storeTableData(stores) {
        // Business logic: Determine channel based on storeType
        // - nacional, regional, independiente → moderno
        // - all others → tradicional
        const MODERNO_STORE_TYPES = ['nacional', 'regional', 'independiente'];

        return (
            stores?.map((store) => {
                // Determine channel from storeType (not from client)
                const storeTypeLower = store.storeType?.toLowerCase() || '';
                const channel = MODERNO_STORE_TYPES.includes(storeTypeLower) ? 'MODERNO' : 'TRADICIONAL';

                // ✅ Format promotor names for display
                const promotorNames = store.promotorNames && store.promotorNames.length > 0 ? store.promotorNames.join(', ') : '';

                return {
                    _id: store._id ?? '',
                    key: store._id ?? '',
                    name: store.name ?? '',
                    channel: channel,
                    rif: store.rif ?? '',
                    clientName: store.clientName ?? '',
                    promotorNames: promotorNames,
                    active: store.active ? 'activo' : 'inactivo',
                    coordinates: store?.coordinates ?? {},
                    image: store?.image ?? '',
                    storeType: store?.storeType ?? ''
                };
            }) || []
        );
    }

    async showDrawer(id) {
        let store = null;
        if (id && typeof id === 'string') {
            store = await this.data.fetchStoreById(id);
            this.setState({ store, open: true });
        }
        this.setOpen(true);
    }

    async bulkDeactivateStores() {
        const { selectedRowKeys } = this.state;
        const { onMessage } = this.props;
        if (!selectedRowKeys?.length) return;

        this.setState({ bulkDeleting: true });
        try {
            const res = await this.data.bulkDeactivateStores(selectedRowKeys);
            if (res?.success) {
                onMessage('success', `${res.deactivated} PDV${res.deactivated > 1 ? 's' : ''} eliminado${res.deactivated > 1 ? 's' : ''} correctamente`);
                const stores = await this.fetchStoresOptimized(true);
                this.setState({ stores, rows: stores, selectedRowKeys: [], bulkDeleting: false });
            } else {
                onMessage('error', 'Error al eliminar los PDVs');
                this.setState({ bulkDeleting: false });
            }
        } catch (err) {
            onMessage('error', 'Error al eliminar los PDVs');
            this.setState({ bulkDeleting: false });
        }
    }

    async deactivateStore(id) {
        if (!id) return;
        const { onMessage } = this.props;

        this.setState({ loading: true });

        const res = await this.data.updateStoreById(id, { active: false });
        if (res) {
            onMessage('success', 'PDV eliminado con éxito!');
            // ✅ Fetch stores with fresh data (cache was invalidated by update)
            const stores = await this.fetchStoresOptimized(true);
            this.setState({ stores, rows: stores, loading: false });
        } else onMessage('error', 'Error al eliminar el cliente');
    }

    async createOrUpdateStore({ name, rif, storeType, client, filteredClients, channel, hasWarehouse, isEdit }) {
        this.setState({ loading: true, disabled: true });
        const { clients, store } = this.state;
        const { onMessage } = this.props;
        if (!name || !rif || !storeType || !client) {
            onMessage('error', 'Todos los campos son requeridos');
            return;
        }

        const payload = {
            name,
            rif: rif?.includes('J') ? rif : `J${rif}`,
            client,
            clientName: clients?.find((c) => c._id === client)?.name,
            products: [],
            storeType: storeType === '' ? (channel === 'moderno' ? ModernStores[0]?.value : TraditionalStores[0]?.value) : storeType,
            hasWarehouse,
            active: true
        };

        const st = isEdit ? await this.data.updateStoreById(store?._id, payload) : await this.data.createStore(payload);
        if (st) {
            onMessage('success', `Se ha ${isEdit ? 'actualizado' : 'creado'} el piso de venta correctamente`);
            // ✅ Fetch stores with fresh data (cache was invalidated by create/update)
            const stores = await this.fetchStoresOptimized(true);
            this.setState({ stores, rows: stores, loading: false, open: false, disabled: false });
        }
    }

    async createMultipleStores({ stores }) {
        this.setState({ disabled: true });
        const { onMessage } = this.props;

        const chunkSize = 5; // Adjust based on performance
        const storeChunks = [];
        const errors = [];

        for (let i = 0; i < stores.length; i += chunkSize) storeChunks.push(stores.slice(i, i + chunkSize));

        let successCount = 0;

        try {
            for (const chunk of storeChunks) {
                const results = await Promise.allSettled(
                    chunk.map(async (st) => {
                        try {
                            const result = await this.data.createStore(st);
                            if (!result) {
                                throw new Error(`Failed to create store: ${st.name}`);
                            }
                            return result;
                        } catch (error) {
                            errors.push({ store: st.name, error: error.message });
                            throw error;
                        }
                    })
                );

                successCount += results.filter((result) => result.status === 'fulfilled').length;
            }

            if (successCount === stores.length) {
                onMessage('success', `Se han creado ${successCount} pisos de venta correctamente`);
            } else {
                const errorMessage =
                    errors.length > 0
                        ? `Cargaron ${successCount} de ${stores?.length} pisos de venta. Errores: ${errors
                              .slice(0, 3)
                              .map((e) => e.store)
                              .join(', ')}${errors.length > 3 ? '...' : ''}`
                        : `Cargaron ${successCount} de ${stores?.length} pisos de venta, verifica que todo esté bien`;
                onMessage('error', errorMessage);
            }
        } catch (error) {
            onMessage('error', 'Error inesperado durante la carga masiva. Intenta de nuevo.');
        } finally {
            this.setState({ open: false, disabled: false });
        }
    }

    async loadMapRecency(dateRange) {
        // Backends without store-check recency show the map without it (StoresMap showRecency).
        if (!isFeatureEnabled(FEATURES.STORE_CHECK_RECENCY)) return;
        const range = dateRange || [dayjs().subtract(30, 'day'), dayjs()];
        const startDate = range[0].format('YYYY-MM-DD');
        const endDate = range[1].format('YYYY-MM-DD');

        this.setState({ mapRecencyLoading: true });
        try {
            const response = await this.data.fetchStoreCheckRecency(startDate, endDate);
            this.setState({
                visitedStoreIds: response?.visitedStoreIds || [],
                mapDateRange: range,
                mapRecencyLoading: false,
            });
        } catch (error) {
            this.props.onMessage?.('error', 'Error al cargar actividad de storechecks en el mapa');
            this.setState({ visitedStoreIds: [], mapDateRange: range, mapRecencyLoading: false });
        }
    }

    async onMapDateRangeChange(dates) {
        if (!dates || dates.length !== 2) return;
        await this.loadMapRecency(dates);
    }

    async onTabChange(tab) {
        this.setTab(tab);
        if (tab === 1 && !this.state.mapDateRange) {
            await this.loadMapRecency();
        }
    }
}

export default StoresController;
